import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, X, Shield, Database, Users, FileSearch, Link as LinkIcon, Cog, Zap } from 'lucide-react';

const comparisonData = [
  {
    capability: 'Enterprise Knowledge Integration',
    ufficient: 'Deep integration with internal docs & databases',
    others: 'Limited to surface-level chat responses',
    icon: Database,
    category: 'integration'
  },
  {
    capability: 'File Storage & Sync',
    ufficient: 'Native file + folder storage with version control',
    others: 'No native storage, relies on external apps',
    icon: FileSearch,
    category: 'storage'
  },
  {
    capability: 'Role-Based Access Control (RBAC)',
    ufficient: 'Built-in permissions, MFA, and audit trails',
    others: 'No enterprise-grade access control',
    icon: Users,
    category: 'security'
  },
  {
    capability: 'Requirements Extraction',
    ufficient: 'Reads & extracts RFP / project needs for EPC, Rail, Construction',
    others: 'Not designed for complex industry workflows',
    icon: Cog,
    category: 'automation'
  },
  {
    capability: 'Citations & Source Linking',
    ufficient: 'Always linked back to source documents',
    others: 'No reliable source attribution',
    icon: LinkIcon,
    category: 'accuracy'
  },
  {
    capability: 'Process Automation',
    ufficient: 'Industry modules: BidAI, RecruitAI, Report Builder',
    others: 'Basic task productivity only',
    icon: Zap,
    category: 'automation'
  },
  {
    capability: 'Security & Compliance',
    ufficient: 'GDPR-ready, SOC2-Ready, AES-256 encryption',
    others: 'Basic or none',
    icon: Shield,
    category: 'security'
  }
];

const ComparisonTable = () => {
  const [hoveredRow, setHoveredRow] = useState(null);

  return (
    <section className="py-24 bg-gradient-to-br from-white via-gray-50 to-blue-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-4"
          >
            <span className="text-blue-600 font-semibold text-sm sm:text-base uppercase tracking-wide">
              What Makes Us Best
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight"
          >
            Why Ufficient <span className="text-blue-600">Outperforms</span> Other AI Tools
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl md:text-2xl font-semibold text-gray-700 max-w-3xl mx-auto"
          >
            Built for Enterprises, Not Just Chat
          </motion.p>
        </div>

        {/* Mobile Cards - Hidden on desktop */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="block md:hidden"
        >
          <div className="space-y-4">
            {comparisonData.map((row, index) => {
              const Icon = row.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
                >
                  {/* Card Header */}
                  <div className="p-4 bg-gradient-to-r from-gray-50 to-blue-50 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon size={20} className="text-blue-600" />
                      </div>
                      <h4 className="font-bold text-gray-900 text-lg">{row.capability}</h4>
                    </div>
                  </div>

                  {/* Card Content */}
                  <div className="p-4 space-y-4">
                    {/* Ufficient */}
                    <div className="border-l-4 border-green-500 pl-4 bg-green-50 rounded-r-lg py-3">
                      <div className="flex items-start space-x-3">
                        <div className="flex items-center space-x-2 mb-2">
                          <img src="/Logo 1.png" alt="Ufficient" className="h-6 w-auto" />
                          <Check size={18} className="text-green-500" />
                        </div>
                      </div>
                      <p className="text-sm text-gray-800 leading-relaxed font-medium">{row.ufficient}</p>
                    </div>

                    {/* Others */}
                    <div className="border-l-4 border-red-500 pl-4 bg-red-50 rounded-r-lg py-3">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-sm font-semibold text-gray-700">Other AI Tools</span>
                        <X size={18} className="text-red-500" />
                      </div>
                      <p className="text-sm text-gray-600 leading-relaxed">{row.others}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Desktop Table - Hidden on mobile */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="hidden md:block"
        >
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
            {/* Table Header */}
            <div className="grid grid-cols-3 gap-4 p-6 bg-gradient-to-r from-gray-50 to-blue-50 border-b border-gray-200">
              <div className="flex items-center">
                <h3 className="text-lg font-bold text-gray-900">Capability</h3>
              </div>
              <div className="flex items-center justify-center">
                <div className="flex items-center space-x-2">
                  <img src="/Logo 1.png" alt="Ufficient" className="h-8 w-auto" />
                  <Check size={24} className="text-green-500" />
                </div>
              </div>
              <div className="flex items-center justify-center">
                <div className="flex items-center space-x-2">
                  <span className="text-lg font-semibold text-gray-700">Other AI Tools</span>
                  <X size={24} className="text-red-500" />
                </div>
              </div>
            </div>

            {/* Table Rows */}
            <div className="divide-y divide-gray-100">
              {comparisonData.map((row, index) => {
                const Icon = row.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className={`grid grid-cols-3 gap-4 p-6 transition-all duration-300 ${
                      hoveredRow === index ? 'bg-blue-50' : 'hover:bg-gray-50'
                    }`}
                    onMouseEnter={() => setHoveredRow(index)}
                    onMouseLeave={() => setHoveredRow(null)}
                  >
                    {/* Capability */}
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon size={20} className="text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 text-base">{row.capability}</h4>
                      </div>
                    </div>

                    {/* Ufficient */}
                    <div className="flex items-center space-x-3">
                      <Check size={20} className="text-green-500 flex-shrink-0" />
                      <p className="text-sm text-gray-700 leading-relaxed">{row.ufficient}</p>
                    </div>

                    {/* Others */}
                    <div className="flex items-center space-x-3">
                      <X size={20} className="text-red-500 flex-shrink-0" />
                      <p className="text-sm text-gray-600 leading-relaxed">{row.others}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ComparisonTable; 